/* eslint-disable react/prop-types */
/**
 * Admin → Entities — maintain the shared entity master (BD.Admin).
 *
 * Entities are the Buyer/Seller vocabulary shared across every deal (see the
 * deal-detail Entities tab, whose `entity_id` typeahead reads the active-only
 * `searchEntities` list). This admin screen is the single place to curate that
 * master list: add a new entity, rename an existing one, and activate /
 * deactivate (soft delete) — there is NO hard delete, because deals reference
 * entities by id and history must stay intact.
 *
 * Deactivating flips `active=false`: the row stays listed here (greyed + tagged
 * INACTIVE) so an admin can reactivate it, but it drops out of the active-only
 * Buyer/Seller picker. A Popconfirm guards deactivation; reactivation is a
 * single click.
 *
 * The same toggle exists in bulk, over the grid's checkbox selection. It is
 * not a convenience: an import once landed 1,334 of the 1,345 entities
 * inactive at a stroke, and the only cure on offer was 1,334 clicks. The
 * server takes `ENTITY_BULK_ACTIVE_CHUNK` ids per call, so a larger selection
 * is chunked here and the per-chunk results are added up into one message.
 *
 * Gating is UI/UX only — the de-hub `/entities` endpoints are the real boundary
 * (`PUT /entities/{id}` is admin-only). We redirect non-admins to `/` as
 * defense-in-depth for the route, mirroring the Pick Lists page.
 */
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from "@azure/msal-react";
import {
  Alert,
  Button,
  Checkbox,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Space,
  Tag,
  Typography,
  message,
  notification,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  StopOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router";
import useMeasure from "react-use-measure";

import {
  WigAntLayout,
  WigAntTable,
} from "@warwick-investment-group/wig-react-library";

import SignIn from "../../signIn";
import { AppContext } from "../../../AppContextProvider";
import usePermissions from "../../../hooks/usePermissions";
import dealsService from "../../../services/dealsService";
import { apiErrorText } from "../../../utils/apiError";

const { Title } = Typography;

// Empty form template — only `name` is user-editable here.
const EMPTY_ENTITY = { name: "" };

/**
 * Ids per bulk call. Must not exceed de-hub's `ENTITY_BULK_ACTIVE_MAX` (500),
 * which 422s a larger list rather than truncating it. Kept a little under so a
 * cap change on the server does not break this screen the day it ships.
 */
const ENTITY_BULK_ACTIVE_CHUNK = 400;

/** Split a list into fixed-size chunks. */
const chunk = (list, size) => {
  const out = [];
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
  return out;
};

/**
 * EntityFormModal — single Add/Rename modal. The parent owns visibility and the
 * row under edit; this component is layout + validation only.
 */
const EntityFormModal = ({
  open,
  mode, // 'add' | 'edit'
  initialValues,
  saving,
  onCancel,
  onSubmit,
}) => {
  const [form] = Form.useForm();

  // Re-seed the form whenever the modal opens or the row under edit changes.
  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue(initialValues || EMPTY_ENTITY);
    }
  }, [open, initialValues, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      await onSubmit(values, { setFieldError: form.setFields });
    } catch {
      // validation / onSubmit errors surfaced inline by AntD or by the parent.
    }
  };

  return (
    <Modal
      open={open}
      title={mode === "add" ? "Add entity" : "Rename entity"}
      onCancel={onCancel}
      onOk={handleOk}
      confirmLoading={saving}
      okText={mode === "add" ? "Create" : "Save"}
      destroyOnHidden
      width={520}
    >
      <Form form={form} layout="vertical" initialValues={EMPTY_ENTITY}>
        <Form.Item
          label="Name"
          name="name"
          rules={[{ required: true, message: "Name is required" }]}
          tooltip="Display name. The server normalizes it for de-duplication — a name that normalizes to an existing entity is rejected as a duplicate."
        >
          <Input autoFocus placeholder="e.g. Acme Minerals, LLC" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const Entities = () => {
  const navigate = useNavigate();
  const [ref, bounds] = useMeasure();
  const { token, getToken, isTokenExpired, user, theme } =
    useContext(AppContext);
  const { can } = usePermissions();
  const allowed = can("admin.entities");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(true);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add"); // 'add' | 'edit'
  const [editingRow, setEditingRow] = useState(null);
  const [saving, setSaving] = useState(false);

  // Bulk activate / deactivate over the grid selection.
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [bulkRunning, setBulkRunning] = useState(false);

  // Redirect users who lack the permission (defense-in-depth for the route;
  // the API is the real boundary).
  useEffect(() => {
    if (!allowed) navigate("/", { replace: true });
  }, [allowed, navigate]);

  const getAuth = useCallback(async () => {
    const userOid = user?.idTokenClaims?.oid || user?.localAccountId;
    if (!userOid) throw new Error("OID not found in user claims.");
    let tk = token;
    if (!tk || isTokenExpired(tk)) tk = await getToken();
    return { token: tk, userOid };
  }, [token, getToken, isTokenExpired, user]);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMsg(null);
      const { token: tk, userOid } = await getAuth();
      const resp = await dealsService.listAllEntities({
        token: tk,
        userOid,
        includeInactive,
      });
      const data = resp?.data?.data;
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error loading entities:", err);
      setErrorMsg(err?.message || String(err));
    } finally {
      setLoading(false);
    }
  }, [getAuth, includeInactive]);

  useEffect(() => {
    if (allowed) refresh();
  }, [allowed, refresh]);

  // ---------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------

  const handleCreate = async (values, helpers) => {
    setSaving(true);
    try {
      const { token: tk, userOid } = await getAuth();
      await dealsService.createEntity({
        token: tk,
        userOid,
        name: values.name?.trim(),
      });
      message.success("Entity created");
      setModalOpen(false);
      await refresh();
    } catch (err) {
      handleSaveError(err, helpers);
    } finally {
      setSaving(false);
    }
  };

  const handleRename = async (values, helpers) => {
    setSaving(true);
    try {
      const { token: tk, userOid } = await getAuth();
      await dealsService.updateEntity({
        token: tk,
        userOid,
        id: editingRow.id,
        body: { name: values.name?.trim() },
      });
      message.success("Entity renamed");
      setModalOpen(false);
      await refresh();
    } catch (err) {
      handleSaveError(err, helpers);
    } finally {
      setSaving(false);
    }
  };

  // Surface a save error either inline on `name` (the 409 duplicate case) or as
  // a top-level notification otherwise.
  const handleSaveError = (err, helpers) => {
    const status = err?.response?.status;
    const detail = apiErrorText(err, { fallback: "Save failed" });

    if (status === 409) {
      helpers?.setFieldError?.([{ name: "name", errors: [String(detail)] }]);
      return;
    }
    notification.error({
      message: "Entity save failed",
      description: String(detail),
    });
  };

  // Activate / deactivate — a soft toggle on `active`. No hard delete.
  const handleToggleActive = async (row) => {
    try {
      const { token: tk, userOid } = await getAuth();
      await dealsService.updateEntity({
        token: tk,
        userOid,
        id: row.id,
        body: { active: !row.active },
      });
      message.success(row.active ? "Entity deactivated" : "Entity activated");
      await refresh();
    } catch (err) {
      const detail = apiErrorText(err);
      notification.error({
        message: row.active
          ? "Failed to deactivate entity"
          : "Failed to activate entity",
        description: detail,
      });
    }
  };

  /**
   * Bulk activate / deactivate the selected rows.
   *
   * Chunked because the server caps a call at 500 ids. The chunks run in
   * sequence rather than in parallel: this is an admin action on up to a
   * couple of thousand rows, and a burst of concurrent writes to the same
   * table buys nothing a user would notice while making a partial failure
   * much harder to describe.
   *
   * A chunk that throws stops the run — the rows already committed stay
   * committed, and the message says how far it got, because silently carrying
   * on would report a total that no longer means anything.
   */
  const handleBulkSetActive = async (active) => {
    const ids = selectedKeys
      .map((k) => Number(k))
      .filter((n) => Number.isFinite(n));
    if (!ids.length) return;

    setBulkRunning(true);
    let updated = 0;
    const skipped = [];
    let failure = null;

    try {
      const { token: tk, userOid } = await getAuth();
      for (const batch of chunk(ids, ENTITY_BULK_ACTIVE_CHUNK)) {
        try {
          const resp = await dealsService.bulkSetEntitiesActive({
            token: tk,
            userOid,
            entityIds: batch,
            active,
          });
          const body = resp?.data || {};
          updated += Number(body.updated) || 0;
          if (Array.isArray(body.skipped)) skipped.push(...body.skipped);
        } catch (err) {
          failure = apiErrorText(err);
          break;
        }
      }
    } catch (err) {
      failure = apiErrorText(err);
    }

    const verb = active ? "activated" : "deactivated";

    if (failure) {
      notification.error({
        message: `Bulk ${active ? "activate" : "deactivate"} failed`,
        description: updated
          ? `${updated} of ${ids.length} ${verb} before the error: ${failure}`
          : failure,
      });
    } else if (updated === 0) {
      // Not an error: every selected row was already in the target state.
      message.info(
        `Nothing to change — ${ids.length === 1 ? "that entity is" : "those entities are"} already ${active ? "active" : "inactive"}.`
      );
    } else {
      message.success(`${updated} ${updated === 1 ? "entity" : "entities"} ${verb}`);
    }

    if (skipped.length) {
      notification.warning({
        message: `${skipped.length} skipped`,
        description: skipped
          .slice(0, 5)
          .map((s) => s?.reason)
          .filter(Boolean)
          .join("; "),
      });
    }

    setSelectedKeys([]);
    setBulkRunning(false);
    await refresh();
  };

  // ---------------------------------------------------------------------
  // Grid wiring
  // ---------------------------------------------------------------------

  const openAdd = () => {
    setModalMode("add");
    setEditingRow(null);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setModalMode("edit");
    setEditingRow(row);
    setModalOpen(true);
  };

  const fmtDate = (v) => {
    if (!v) return "";
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
  };

  const columns = useMemo(
    () => [
      {
        dataIndex: "name",
        title: "Name",
        key: "name",
        sorter: (a, b) =>
          String(a?.name ?? "").localeCompare(String(b?.name ?? "")),
        render: (value, row) =>
          row?.active ? (
            value
          ) : (
            <span style={{ opacity: 0.55 }}>{value}</span>
          ),
      },
      {
        dataIndex: "active",
        title: "Active",
        key: "active",
        width: 110,
        sorter: (a, b) => Number(a?.active) - Number(b?.active),
        render: (value) => (
          <Tag color={value ? "green" : "default"}>
            {value ? "ACTIVE" : "INACTIVE"}
          </Tag>
        ),
      },
      {
        dataIndex: "created_by",
        title: "Created by",
        key: "created_by",
        width: 160,
      },
      {
        dataIndex: "created_at",
        title: "Created",
        key: "created_at",
        width: 190,
        sorter: (a, b) =>
          String(a?.created_at ?? "").localeCompare(
            String(b?.created_at ?? "")
          ),
        render: (value) => fmtDate(value),
      },
      {
        dataIndex: "updated_by",
        title: "Updated by",
        key: "updated_by",
        width: 160,
      },
      {
        dataIndex: "updated_at",
        title: "Updated",
        key: "updated_at",
        width: 190,
        sorter: (a, b) =>
          String(a?.updated_at ?? "").localeCompare(
            String(b?.updated_at ?? "")
          ),
        render: (value) => fmtDate(value),
      },
      {
        title: "Actions",
        key: "actions",
        width: 130,
        fixed: "right",
        align: "center",
        render: (_, row) => {
          if (!row) return null;
          return (
            <Space size="small" onClick={(e) => e.stopPropagation()}>
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => openEdit(row)}
                aria-label={`Rename ${row.name}`}
              />
              {row.active ? (
                <Popconfirm
                  title="Deactivate entity?"
                  description={`Deactivate "${row.name}"? It stays on file but won't appear in the Buyer/Seller picker.`}
                  okText="Deactivate"
                  okType="danger"
                  cancelText="Cancel"
                  onConfirm={() => handleToggleActive(row)}
                >
                  <Button
                    size="small"
                    danger
                    icon={<StopOutlined />}
                    aria-label={`Deactivate ${row.name}`}
                  />
                </Popconfirm>
              ) : (
                <Button
                  size="small"
                  icon={<CheckCircleOutlined />}
                  onClick={() => handleToggleActive(row)}
                  aria-label={`Activate ${row.name}`}
                />
              )}
            </Space>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Initial values for the modal — for Rename we hydrate name from the row.
  const modalInitialValues = useMemo(() => {
    if (modalMode === "edit" && editingRow) {
      return { name: editingRow.name ?? "" };
    }
    return EMPTY_ENTITY;
  }, [modalMode, editingRow]);

  const gridHeight = Math.max(bounds.height - 220, 400);

  // `selections: true` adds antd's "Select all data" / invert / none menu to
  // the header checkbox. The bare checkbox only takes the current page, which
  // is no use against a thousand-row repair.
  const rowSelection = useMemo(
    () => ({
      selectedRowKeys: selectedKeys,
      onChange: setSelectedKeys,
      selections: true,
      preserveSelectedRowKeys: true,
    }),
    [selectedKeys]
  );

  const selectedCount = selectedKeys.length;

  if (!allowed) return null;

  return (
    <React.Fragment>
      <AuthenticatedTemplate>
        <Row ref={ref}>
          <WigAntLayout
            darkMode={theme === "dark"}
            layoutStyle={{
              height: "93vh",
              width: "99vw",
              margin: 0,
              padding: 0,
              overflow: "hidden",
            }}
            contentStyle={{
              minHeight: "70vh",
              overflow: "auto",
              padding: 12,
              margin: 0,
            }}
            showFooter={false}
            showHeader={false}
            siderProps={{ collapsible: true, collapsed: true, width: 0 }}
            showDefaultMenu={false}
            content={
              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <Row justify="space-between" align="middle">
                  <Title level={3} style={{ margin: 0 }}>
                    Entities
                  </Title>
                  {/* Bulk actions sit in the title row and stay visible but
                      disabled with nothing selected, the same shape the deals
                      grid uses for Bulk edit / Delete. The count is the whole
                      selection: activating rows that are already active is a
                      no-op server-side, so there is no need to make the user
                      reason about which half of their selection each button
                      applies to. */}
                  <Space>
                    <Checkbox
                      checked={includeInactive}
                      onChange={(e) => setIncludeInactive(e.target.checked)}
                    >
                      Show inactive
                    </Checkbox>
                    <Button
                      icon={<CheckCircleOutlined />}
                      loading={bulkRunning}
                      disabled={selectedCount === 0}
                      onClick={() => handleBulkSetActive(true)}
                      // Explicit, because AntD folds the icon's own label into
                      // the accessible name ("check-circle Activate (2)").
                      aria-label={`Activate ${selectedCount} selected`}
                    >
                      Activate ({selectedCount})
                    </Button>
                    <Popconfirm
                      title="Deactivate entities?"
                      description={`Deactivate ${selectedCount} selected ${
                        selectedCount === 1 ? "entity" : "entities"
                      }? They stay on file but won't appear in the Buyer/Seller picker.`}
                      okText="Deactivate"
                      okType="danger"
                      cancelText="Cancel"
                      disabled={selectedCount === 0 || bulkRunning}
                      onConfirm={() => handleBulkSetActive(false)}
                    >
                      <Button
                        danger
                        icon={<StopOutlined />}
                        loading={bulkRunning}
                        disabled={selectedCount === 0}
                        aria-label={`Deactivate ${selectedCount} selected`}
                      >
                        Deactivate ({selectedCount})
                      </Button>
                    </Popconfirm>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={openAdd}
                    >
                      Add Entity
                    </Button>
                  </Space>
                </Row>

                {errorMsg && (
                  <Alert
                    type="error"
                    showIcon
                    message="Failed to load entities"
                    description={errorMsg}
                  />
                )}

                {!loading && rows.length === 0 && !errorMsg ? (
                  <Alert
                    type="info"
                    showIcon
                    message="No entities yet"
                    description="There are no entity records. Click Add Entity to create the first one."
                  />
                ) : (
                  <WigAntTable
                    dataSource={rows}
                    columns={columns}
                    rowKey={(record) =>
                      record?.id != null ? String(record.id) : undefined
                    }
                    rowSelection={rowSelection}
                    loading={loading}
                    showSearch
                    searchWidth={300}
                    showActionButton={false}
                    showExport
                    exportOptions={{ filename: "entities" }}
                    showPagination
                    pagination={{
                      defaultPageSize: 100,
                      showSizeChanger: true,
                      pageSizeOptions: ["10", "25", "50", "100"],
                    }}
                    tableWidth="100%"
                    scroll={{ y: gridHeight }}
                  />
                )}

                <EntityFormModal
                  open={modalOpen}
                  mode={modalMode}
                  initialValues={modalInitialValues}
                  saving={saving}
                  onCancel={() => setModalOpen(false)}
                  onSubmit={modalMode === "add" ? handleCreate : handleRename}
                />
              </Space>
            }
          />
        </Row>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <SignIn />
      </UnauthenticatedTemplate>
    </React.Fragment>
  );
};

export default Entities;
